import { DataSourceDto, GenericTableConfigPatchDto } from 'inside-fifa/api/generic-tables/dto';
import { useCallback, useEffect, useState } from 'react';
import { ConfigUpdateColumnsConflictError } from 'inside-fifa/api/generic-tables/update-config';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { LoadingButton } from '@mui/lab';

type DataSourceChangeConfirmModalProps = {
    newDataSource: DataSourceDto | null;
    pendingChanges: boolean;
    onCancel: () => void;
    onSubmit: (data: Omit<GenericTableConfigPatchDto, 'modifiedTimestamp'>) => Promise<void>;
};

export const DataSourceChangeConfirmModal = ({
    newDataSource,
    pendingChanges,
    onCancel,
    onSubmit,
}: DataSourceChangeConfirmModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [newDataSourceHasConflict, setNewDataSourceHasConflict] = useState(false);

    useEffect(() => {
        setNewDataSourceHasConflict(false);
    }, [newDataSource]);

    const onConfirm = useCallback(async () => {
        if (!newDataSource) {
            return;
        }

        setIsLoading(true);

        try {
            await onSubmit({
                dataSource: newDataSource,
                force: newDataSourceHasConflict,
            });
        } catch (error) {
            if (error instanceof ConfigUpdateColumnsConflictError) {
                setNewDataSourceHasConflict(true);
                return;
            }

            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [newDataSource, newDataSourceHasConflict, onSubmit]);

    return (
        <Dialog open={Boolean(newDataSource)}>
            <DialogTitle>
                <FormattedMessage
                    id={
                        newDataSourceHasConflict
                            ? 'inside-fifa.generic-tables.data-source-change-conflict-title'
                            : 'inside-fifa.generic-tables.data-source-change-warn.title'
                    }
                />
            </DialogTitle>
            <DialogContent dividers>
                {newDataSourceHasConflict ? (
                    <FormattedMessage id="inside-fifa.generic-tables.data-source-change-conflict-content" />
                ) : (
                    <>
                        <FormattedMessage id="inside-fifa.generic-tables.data-source-change-warn-content" />
                        <p>
                            {pendingChanges && (
                                <FormattedMessage id="inside-fifa.generic-tables.data-source-change-warn-content-on-pending-changes" />
                            )}
                        </p>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={onCancel} color="error">
                    <FormattedMessage id="inside-fifa.cancel" />
                </Button>
                <LoadingButton onClick={onConfirm} loading={isLoading} color="primary">
                    <FormattedMessage id="inside-fifa.proceed" />
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};
